// Системная кнопка «Назад» внутри многошагового флоу (RBXGD-1110).
//
// До этого шаги покупки жили только в локальном состоянии компонента, в историю
// браузера не попадали, и «Назад» выкидывал человека со всего флоу на
// предыдущую страницу — вместе с введёнными данными. На мобилке это самый
// привычный жест, то есть он ломал воронку в самом денежном месте.
//
// Перенос механики с Дерева (src/shared/hooks/useStepBackButton.ts, URL-режим),
// переписанный под Vue 2: composition-api в проекте нет.
//
// Идея: шаг кодируется в hash (#f=<токен>), и источником правды становится сама
// история браузера, а не наш массив. Переход вперёд добавляет запись, «Назад»
// отдаёт предыдущий токен через popstate, мы под него восстанавливаем UI.
//
// Компонент, который подключает миксин, обязан объявить:
//   computed.stepToken   — токен текущего шага; '' означает базовый шаг, с него
//                          «Назад» уводит из флоу, как и ожидает пользователь;
//   methods.restoreStep(token) — восстановить UI под токен без навигации.
const HASH_PREFIX = '#f='

const readHashToken = () => {
  const hash = window.location.hash

  if (!hash.startsWith(HASH_PREFIX)) return ''

  return decodeURIComponent(hash.slice(HASH_PREFIX.length))
}

export default {
  methods: {
    // Стереть шаг из адреса, не добавляя записи в историю. Нужно, когда шаг
    // сменился не переходом «назад», а нашим решением — иначе в URL остаётся
    // токен от предыдущего шага и адрес врёт о том, что на экране.
    clearStepHash() {
      if (!readHashToken()) return

      window.history.replaceState(
        { ...window.history.state },
        '',
        window.location.pathname + window.location.search
      )
    }
  },
  watch: {
    stepToken: {
      // sync обязателен. Обычный вотчер Vue 2 отложенный, и две смены шага в
      // одном тике он схлопывает: промежуточный шаг записи в историю не
      // получает, а «Назад» его перепрыгивает или внешне не делает ничего.
      // В реальном флоу так и происходит — signInSuccess ставит шаг через
      // $nextTick, часть ветвей changeTransfer меняет его дважды.
      //
      // Проверено на месте: с sync вотчер видит оба значения, без него —
      // только последнее. У Дерева то же самое сделано через flush: 'sync'.
      // Заодно запись добавляется синхронно внутри клика, а этого требует
      // Android — отложенную он теряет.
      sync: true,
      handler(token) {
        // До монтирования истории у флоу ещё нет: компонент только выбирает
        // стартовый шаг, разбирая роут и query. Промежуточные значения оттуда
        // записывать нельзя — это мусорные записи, через которые потом надо
        // продираться «Назад». А поскольку вотчер синхронный, он успевает
        // сработать прямо в created, то есть до того, как появится __stepStack.
        if (!this.__stepMounted) return

        // После restore токен уже совпадает с hash — второй раз записывать его
        // нельзя, иначе «Назад» пришлось бы жать дважды на каждый шаг.
        if (token === readHashToken()) return

        // У шагов есть свои кнопки «Назад» в интерфейсе, и они двигают шаг
        // НАЗАД (например, экран выбора платёжки уводит на выбор плейса). Такую
        // смену нельзя писать новой записью: в истории появлялся дубль, из-за
        // которого браузерный «Назад» сначала уходил вперёд, а следующее
        // нажатие попадало на запись с текущим же шагом и не делало ничего.
        //
        // Поэтому ведём свой стек записей и, если шаг уже в нём есть, откатываем
        // историю до него вместо добавления. Токены могут повторяться (A→B→A),
        // поэтому ищем последнее вхождение.
        const stack = this.__stepStack

        // Где новый шаг стоит в нашей цепочке записей. Три случая, и путать их
        // нельзя — на этом я ошибся дважды, потому что lastIndexOf отдаёт -1
        // и для «нет в цепочке», а -1 в этой же логике значит «базовый шаг».
        //
        //   -1    — базовый шаг, своей записи у него нет
        //   число — шаг уже в цепочке, значит это возврат
        //   null  — шага в цепочке нет, значит это переход вперёд
        let position = null

        if (!token) position = -1
        else {
          const found = stack.lastIndexOf(token)

          if (found !== -1) position = found
        }

        // Переход вперёд. Пишем КАЖДЫЙ шаг, включая те, на которые возвращаться
        // нельзя, — их отфильтрует restoreStep. «Ловушки» без смены адреса не
        // годились: они оставались в истории мёртвыми, когда флоу уходил дальше.
        if (position === null) {
          stack.push(token)
          window.history.pushState({ ...window.history.state }, '', HASH_PREFIX + encodeURIComponent(token))
          return
        }

        const назад = stack.length - 1 - position

        // Уже стоим на этой записи — двигать нечего.
        if (назад <= 0) return

        stack.length = position + 1
        // popstate после этого увидит совпадение токена с шагом и ничего не
        // сделает: шаг уже установлен, откатить надо только адрес.
        window.history.go(-назад)
      }
    }
  },
  mounted() {
    // Флоу всегда начинается с базового шага, поэтому устаревший hash из
    // прошлого визита стираем. path и query сохраняем: там utm и промокод.
    if (readHashToken()) {
      window.history.replaceState(
        { ...window.history.state },
        '',
        window.location.pathname + window.location.search
      )
    }

    // Наш слепок записей истории: браузер их перечислить не даёт, а знать, где
    // в цепочке стоит шаг, нужно — иначе не отличить возврат от перехода вперёд.
    this.__stepStack = []

    this.__onStepPopState = () => {
      // Браузер уже сменил hash — подтягиваем шаг под него.
      const token = readHashToken()

      // Стек держим в согласии с адресом: после отката лишние записи ушли.
      // Неизвестный токен стек не трогает — иначе затрём цепочку.
      const found = token ? this.__stepStack.lastIndexOf(token) : -1

      if (!token) this.__stepStack.length = 0
      else if (found !== -1) this.__stepStack.length = found + 1

      if (token !== this.stepToken) this.restoreStep(token)
    }

    window.addEventListener('popstate', this.__onStepPopState)

    // Стартовый шаг выбран, дальше шаги меняет уже человек — с этого момента их
    // и записываем. Стартовому запись не нужна: возвращаться внутри флоу с него
    // некуда, и «Назад» должен уводить со страницы.
    this.__stepMounted = true
  },
  beforeDestroy() {
    if (this.__onStepPopState) window.removeEventListener('popstate', this.__onStepPopState)
  }
}
